import React from "react";
import {
  downloadCSV,
  useDataProvider,
  useListContext,
  useNotify,
  useTranslate,
} from "react-admin";
import jsonexport from "jsonexport/dist";
import { Box, Button } from "@mui/material";
import {
  PictureAsPdfOutlined as PdfIcon,
  FileDownloadOutlined as CsvIcon,
} from "@mui/icons-material";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { extractErrorMessage } from "@/utils/extractErrorMessage";

const RESOURCE = "credit-consumption/credit-transaction";
const i18nBase = "resources.credit-consumption/credit-transaction";

interface TxRecord {
  accountName?: string;
  costCenterName?: string;
  transactionType?: string;
  amount?: number;
  balanceAfter?: number;
  createdAt?: string;
  createdByUserName?: string;
}

/**
 * Export the Credit Transactions (all rows matching the active filters, not
 * just the current page) to CSV or PDF. Rendered inside the List so it reads
 * the live filter/sort from the list context.
 */
export const CreditTransactionExportButtons: React.FC = () => {
  const dataProvider = useDataProvider();
  const { filterValues, sort } = useListContext();
  const notify = useNotify();
  const translate = useTranslate();
  const [busy, setBusy] = React.useState(false);

  const fetchAll = async (): Promise<TxRecord[]> => {
    const { data } = await dataProvider.getList<TxRecord & { id: string }>(
      RESOURCE,
      {
        pagination: { page: 1, perPage: 10000 },
        sort: sort ?? { field: "createdAt", order: "DESC" },
        filter: filterValues ?? {},
      },
    );
    return data;
  };

  const cols = [
    { key: "account", label: translate(`${i18nBase}.fields.account`, { _: "Account" }) },
    { key: "costCenter", label: translate(`${i18nBase}.fields.costCenterName`, { _: "Cost Center" }) },
    { key: "type", label: translate(`${i18nBase}.fields.transactionType`, { _: "Type" }) },
    { key: "amount", label: translate(`${i18nBase}.fields.amount`, { _: "Amount" }) },
    { key: "balanceAfter", label: translate(`${i18nBase}.fields.balanceAfter`, { _: "Balance After" }) },
    { key: "date", label: translate(`${i18nBase}.fields.createdAt`, { _: "Date" }) },
    { key: "by", label: translate(`${i18nBase}.fields.createdByUserName`, { _: "By" }) },
  ] as const;

  const toRow = (r: TxRecord): Record<string, string> => ({
    account: r.accountName ?? "",
    costCenter: r.costCenterName ?? "",
    type: r.transactionType ?? "",
    amount: r.amount != null ? String(r.amount) : "",
    balanceAfter: r.balanceAfter != null ? String(r.balanceAfter) : "",
    date: r.createdAt ? new Date(r.createdAt).toLocaleString() : "",
    by: r.createdByUserName ?? "",
  });

  const onError = (err: unknown): void =>
    notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
      type: "error",
    });

  const exportCsv = async (): Promise<void> => {
    setBusy(true);
    try {
      const rows = (await fetchAll()).map(toRow);
      jsonexport(rows, (err: unknown, csv: string) => {
        if (!err) downloadCSV(csv, "credit-transactions");
      });
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const exportPdf = async (): Promise<void> => {
    setBusy(true);
    try {
      const records = await fetchAll();
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(13);
      doc.text(translate(`${i18nBase}.name`, { smart_count: 2 }), 14, 14);
      autoTable(doc, {
        startY: 20,
        head: [cols.map((c) => c.label)],
        body: records.map((r) => {
          const row = toRow(r);
          return cols.map((c) => row[c.key] ?? "");
        }),
        styles: { fontSize: 8 },
        headStyles: { fillColor: [11, 165, 183] },
      });
      doc.save("credit-transactions.pdf");
    } catch (e) {
      onError(e);
    } finally {
      setBusy(false);
    }
  };

  const btnSx = {
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
    flexShrink: 0,
  } as const;

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<CsvIcon />}
        disabled={busy}
        onClick={() => void exportCsv()}
        sx={btnSx}
      >
        {translate(`${i18nBase}.actions.exportCsv`, { _: "CSV" })}
      </Button>
      <Button
        size="small"
        variant="outlined"
        startIcon={<PdfIcon />}
        disabled={busy}
        onClick={() => void exportPdf()}
        sx={btnSx}
      >
        {translate(`${i18nBase}.actions.exportPdf`, { _: "PDF" })}
      </Button>
    </Box>
  );
};

export default CreditTransactionExportButtons;
